import useTranslation from 'next-translate/useTranslation'
import { twMerge } from 'tailwind-merge'

import { FlexRow } from '@unreserved-frontend-v2/ui/flex/flex-row'

import House from './house.svg'
import { PreviewerProps } from './props'
import { ImageWithShimmerOptimized } from '../image-with-shimmer/image-with-shimmer-optimized'

const restrictedUnderlay =
  'after:content-[""] after:w-full after:h-full after:opacity-40 after:absolute after:top-0 after:bg-shades-1000'

export function Previewer({
  images = [],
  showExtendedImages,
  showPlaceholderImage,
  isRestricted,
  restrictedPrompt,
  className,
  isEagerLoaded,
  onImgClick,
  altPrefix = '',
  placeholderClassName,
  imagesContainerClassName,
}: PreviewerProps) {
  const hasImages = images.length >= 1
  const { t } = useTranslation()

  const hasMoreThanThreeImages = images.length >= 3
  const hasExtendedImages = showExtendedImages && images.length >= 5
  let totalImagesDisplayed = hasMoreThanThreeImages ? 3 : 1
  if (hasExtendedImages) {
    totalImagesDisplayed = 5
  }

  return (
    <div data-nosnippet={isRestricted} className={twMerge('relative overflow-hidden rounded-t-md', className)}>
      {hasImages ? (
        <div id="previewer-images-container" className={twMerge('flex h-[460px]', imagesContainerClassName)}>
          <div
            className={twMerge(
              `relative flex-grow overflow-hidden ${images.length > 1 ? 'mr-2' : ''}`,
              isRestricted ? restrictedUnderlay : '',
              onImgClick ? 'cursor-pointer' : null
            )}
            onClick={onImgClick ? () => onImgClick(0) : undefined}
          >
            <ImageWithShimmerOptimized
              id="preview-img-0"
              src={images[0]}
              width={images.length > 1 ? 570 : 866}
              height={460}
              className="restricted-content-2"
              isRestricted={isRestricted}
              isEagerLoaded={isEagerLoaded}
              alt={
                hasMoreThanThreeImages && altPrefix
                  ? `${altPrefix} - ${t('ui:of', { current: 1, total: totalImagesDisplayed })}`
                  : altPrefix
              }
            />
            {isRestricted ? restrictedPrompt : null}
          </div>

          {hasMoreThanThreeImages ? (
            <div className={twMerge('flex flex-col', hasExtendedImages ? 'w-1/4' : 'w-1/3')}>
              <div
                className={twMerge(
                  'relative mb-2 flex-grow',
                  isRestricted ? restrictedUnderlay : '',
                  onImgClick ? 'cursor-pointer' : null
                )}
                onClick={onImgClick ? () => onImgClick(1) : undefined}
              >
                <ImageWithShimmerOptimized
                  id="preview-img-1"
                  src={images[1]}
                  width={308}
                  height={226}
                  isRestricted={isRestricted}
                  isEagerLoaded={isEagerLoaded}
                  alt={altPrefix ? `${altPrefix} - ${t('ui:of', { current: 2, total: totalImagesDisplayed })}` : ''}
                />
              </div>
              <div
                className={twMerge(
                  `relative flex-grow`,
                  isRestricted ? restrictedUnderlay : '',
                  onImgClick ? 'cursor-pointer' : null
                )}
                onClick={onImgClick ? () => onImgClick(2) : undefined}
              >
                <ImageWithShimmerOptimized
                  id="preview-img-2"
                  src={images[2]}
                  width={308}
                  height={226}
                  isRestricted={isRestricted}
                  isEagerLoaded={isEagerLoaded}
                  alt={altPrefix ? `${altPrefix} - ${t('ui:of', { current: 3, total: totalImagesDisplayed })}` : ''}
                />
              </div>
            </div>
          ) : null}

          {hasExtendedImages ? (
            <div className={twMerge('ml-2 flex flex-col', hasExtendedImages ? 'w-1/4' : 'w-1/3')}>
              <div
                className={twMerge(
                  'relative mb-2 flex-grow',
                  isRestricted ? restrictedUnderlay : '',
                  onImgClick ? 'cursor-pointer' : null
                )}
                onClick={onImgClick ? () => onImgClick(3) : undefined}
              >
                <ImageWithShimmerOptimized
                  id="preview-img-1"
                  src={images[3]}
                  width={308}
                  height={226}
                  isRestricted={isRestricted}
                  isEagerLoaded={isEagerLoaded}
                  alt={altPrefix ? `${altPrefix} - ${t('ui:of', { current: 4, total: totalImagesDisplayed })}` : ''}
                />
              </div>
              <div
                className={twMerge(
                  `relative flex-grow`,
                  isRestricted ? restrictedUnderlay : '',
                  onImgClick ? 'cursor-pointer' : null
                )}
                onClick={onImgClick ? () => onImgClick(4) : undefined}
              >
                <ImageWithShimmerOptimized
                  id="preview-img-2"
                  src={images[4]}
                  width={308}
                  height={226}
                  isRestricted={isRestricted}
                  isEagerLoaded={isEagerLoaded}
                  alt={altPrefix ? `${altPrefix} - ${t('ui:of', { current: 5, total: totalImagesDisplayed })}` : ''}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {!hasImages && showPlaceholderImage ? (
        <FlexRow
          id="previewer-placeholder"
          className={twMerge(
            'h-[225px] items-center rounded-t-md border border-shades-200 bg-shades-25',
            placeholderClassName
          )}
        >
          <House className="mx-auto w-[275px]" alt="" />
        </FlexRow>
      ) : null}
    </div>
  )
}

export default Previewer
